import { AfterViewInit, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { cloneDeep } from 'lodash';
import { takeUntil } from 'rxjs/operators';
import {
  AccountService,
  AlertService,
  CampaignProductService,
  CampaignService,
  CampaignTrackingService,
  CancelReasonCategoryService,
  CRMCampaignService,
  CustomerSupportReportService,
  FunnelService,
  FunnelStepService,
  GuiService,
  MerchantService,
  ReportsConfigService,
  SideNavService,
  StorageService,
  UserService,
} from '../_services';
import { MultiOptions } from '../custom-multi-select/multi-select';
import { expandCollapse } from '../_shared';
import { ReportsAbstractV3Component } from './reports-abstract-v3.component';
import {
  csDeviceDimensions,
  csTrafficDimensions,
  CustomerSupportFields,
  CustomerSupportFilters,
  customerSupportMetrics,
  reportsFieldDataV3,
} from './utils';
import { CustomerSupportSummaryResponse, PortalActionRow, ReportDimensions, ReportTypes } from './models/reports';

@Component({
  moduleId: module.id.toString(),
  selector: 'customer-support-report-v3',
  templateUrl: './templates/reports-v3.component.html',
  styleUrls: ['./styles/reports-v2.component.scss', './styles/reports-v3.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [expandCollapse],
})
export class CustomerSupportReportV3Component extends ReportsAbstractV3Component implements OnInit, AfterViewInit {
  fields: MultiOptions = cloneDeep(CustomerSupportFields);
  filters: MultiOptions = cloneDeep(CustomerSupportFilters);
  reportType = ReportTypes.CUSTOMER_SUPPORT;
  showOverviewPanel: boolean = true;
  showTopIntentsPanel: boolean = true;
  showPortalMomentsPanel: boolean = true;
  showPortalActionsPanel: boolean = true;
  summaryExpanded: boolean = true;
  showExcludeUpsellsFilter: boolean = false;
  showExcludeValidationsFilter: boolean = false;
  dropdownsLoading: boolean = false;

  predefinedFields: string[] = [];
  commonReportsFields: string[] = [...customerSupportMetrics];
  summaryFields: string[] = [...customerSupportMetrics];

  pctFieldMap: { [key: string]: string } = {
    successful: 'successful_pct',
    bypass: 'bypass_pct',
    dropoffs: 'dropoffs_pct',
    lifeline: 'lifeline_pct',
    abandoned: 'abandoned_pct',
    inquiries: 'inquiries_pct',
  };

  selectedDimensions = [{ model: ReportDimensions.Channel, label: 'Channel', selected: true, disabled: false }];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected alertService: AlertService,
    public reportsService: CustomerSupportReportService,
    protected modalService: NgxSmartModalService,
    protected campaignService: CampaignService,
    protected reportsConfigService: ReportsConfigService,
    protected trackingSourceService: CampaignTrackingService,
    protected userService: UserService,
    protected accountService: AccountService,
    protected campaignProductService: CampaignProductService,
    protected crmCampaignService: CRMCampaignService,
    protected storageService: StorageService,
    protected stepService: FunnelStepService,
    protected merchantService: MerchantService,
    protected cancelReasonCategoryService: CancelReasonCategoryService,
    protected funnelService: FunnelService,
    protected guiService: GuiService,
    protected sideNavService: SideNavService
  ) {
    super(
      router,
      location,
      route,
      alertService,
      reportsService,
      campaignService,
      reportsConfigService,
      trackingSourceService,
      userService,
      accountService,
      campaignProductService,
      crmCampaignService,
      storageService,
      stepService,
      merchantService,
      modalService,
      cancelReasonCategoryService,
      funnelService,
      guiService
    );
    this.fieldData = reportsFieldDataV3;
    this.updatePreSelectedDimensions(ReportDimensions.Channel);
    this.bannerTitle = 'Customer Support Report';
    this.bannerSubtitle =
      'This report shows how support visits resolve themselves, broken down by channel, device, traffic source and ' +
      'campaign. Use the dimensions and filters to see where self-service is working and where customers drop out.';
  }

  ngOnInit() {
    super.ngOnInit();
    this.sideNavService.setSideNavExpanded(false);
  }

  ngAfterViewInit() {
    if (this.panel) {
      this.panel.expanded = true;
    }
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.selectedDimensions, event.previousIndex, event.currentIndex);
  }

  getQueryForSummary() {
    const query = super.getQueryForSummary();
    query['report_type'] = this.reportType;
    query['fields'] = [];
    return query;
  }

  getQueryForReport() {
    const query = super.getQueryForReport();
    query['report_type'] = this.reportType;
    query['show_percentage'] = this.showPercentage;
    return query;
  }

  // Gateway's loadOverviewCards() is not reusable here: there is no overview_cards endpoint.
  getSummary() {
    this.isSummaryLoading = true;
    this.isOverviewLoading = true;
    this.reportsService
      .getSummary(this.getQueryForSummary())
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (result) => {
          const response = result && result[0] ? result[0] : {};
          this.applySummaryResponse(response);
          this.stopSummaryLoading();
        },
        (error) => {
          this.applySummaryResponse({});
          this.stopSummaryLoading();
          this.handleError(error);
        }
      );
  }

  // The base list is retention-specific; this report fetches its own 8 plain-string menus instead.
  protected getFilterOptionConfig(): ReportDimensions[] {
    const dimensions: [string, string][] = [...csTrafficDimensions, ...csDeviceDimensions];
    return dimensions.map(([model]) => model as ReportDimensions);
  }

  getFilterOptionsData(fields: ReportDimensions[]) {
    super.getFilterOptionsData(fields);
    this.fetchGroupedFilterOptions(ReportDimensions.Intent, ReportDimensions.IntentID, ReportDimensions.IntentName);
    this.fetchGroupedFilterOptions(
      ReportDimensions.SessionResult,
      ReportDimensions.SessionResult,
      ReportDimensions.ResultLabel
    );
    this.fetchGroupedFilterOptions(ReportDimensions.CRMID, ReportDimensions.CRMID, ReportDimensions.CRMName);
  }

  private applySummaryResponse(response: Partial<CustomerSupportSummaryResponse>) {
    const overview = response.overview ? response.overview : {};
    const intents = Array.isArray(response.top_intents) ? response.top_intents : [];
    const moments = Array.isArray(response.portal_moments) ? response.portal_moments : [];
    const actions = this.extractPortalActionRows(response.portal_actions);

    const summary = this.buildSummaryData(overview);
    this.summary = summary;
    this.setOverviewCards(this.buildOverviewCards(customerSupportMetrics, overview, summary));
    this.topIntents = intents;
    this.portalMoments = moments;
    this.portalActions = actions;
  }

  private extractPortalActionRows(portalActions: CustomerSupportSummaryResponse['portal_actions']): PortalActionRow[] {
    let rows: PortalActionRow[] = [];
    if (portalActions && Array.isArray(portalActions.results)) {
      rows = portalActions.results;
    }
    return rows;
  }

  private stopSummaryLoading() {
    this.isSummaryLoading = false;
    this.isOverviewLoading = false;
  }
}
